import React, { useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDanceStore } from '../store/useDanceStore';
import { useStorageStore } from '../store/useStorageStore';
import { Music, Volume2, VolumeX, X, Loader } from 'lucide-react';
import { DanceState } from '../types';

interface MusicPanelProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

export const MusicPanel: FC<MusicPanelProps> = ({ audioRef }) => {
  const { t } = useTranslation();
  const currentModuleId = useStorageStore((s) => s.tutorialProgress.currentModuleId);
  const hasMusicLoaded = useDanceStore((s: DanceState) => s.hasMusicLoaded);
  const isReadOnly = useDanceStore((s: DanceState) => s.isReadOnly);
  const audioUrl = useDanceStore((s: DanceState) => s.audioUrl);
  const audioFileName = useDanceStore((s: DanceState) => s.audioFileName);
  const bpm = useDanceStore((s: DanceState) => s.bpm);
  const beats = useDanceStore((s: DanceState) => s.beats);
  const measures = useDanceStore((s: DanceState) => s.measures);
  const musicVolume = useDanceStore((s: DanceState) => s.musicVolume);
  const isAnalyzing = useDanceStore((s: DanceState) => s.isAnalyzing);
  const isPlaying = useDanceStore((s: DanceState) => s.isPlaying);
  const removeMusic = useDanceStore((s: DanceState) => s.removeMusic);
  const setMusicVolume = useDanceStore((s: DanceState) => s.setMusicVolume);
  const keyframes = useDanceStore((s: DanceState) => s.keyframes);
  const setSoundtrackModalOpen = useDanceStore((s: DanceState) => s.setSoundtrackModalOpen);

  // Sync volume to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume;
    }
  }, [musicVolume, audioRef]);

  const handleMusicButtonClick = () => {
    setSoundtrackModalOpen(true);
  };

  const handleRemoveMusic = () => {
    const executeRemove = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      removeMusic();
    };

    if (keyframes.length > 0) {
      useDanceStore.getState().confirmAction({
        title: t('music.remove_confirm_title'),
        message: t('music.remove_confirm_message'),
        onConfirm: executeRemove,
      });
    } else {
      executeRemove();
    }
  };

  if (isAnalyzing) {
    return (
      <div className="music-panel analyzing">
        <Loader size={14} className="spin-icon" />
        <span style={{ fontSize: '0.75rem', color: '#fde68a' }}>{t('music.analyzing')}</span>
      </div>
    );
  }

  if (!hasMusicLoaded) {
    if (isReadOnly) return null;

    return (
      <div className="music-panel">
        <button
          id="timeline-soundtrack-btn"
          className="timeline-btn purple"
          onClick={handleMusicButtonClick}
          disabled={isPlaying}
          title={t('music.load')}
        >
          <Music size={14} />
          <span style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.5px' }}>{t('music.soundtrack')}</span>
        </button>
      </div>
    );
  }

  // Music is loaded — show info & controls
  return (
    <div className="music-panel loaded">
      <audio ref={audioRef} src={audioUrl || undefined} preload="auto" />

      <Music size={13} color="#a78bfa" />

      <div className="music-info">
        <span className="music-filename" title={audioFileName || ''}>
          {audioFileName && audioFileName.length > 18
            ? audioFileName.slice(0, 15) + '...'
            : audioFileName}
        </span>
        <span className="music-bpm">{bpm} BPM</span>
        <span className="music-beats">{beats.length}♩ · {t('music.bars', { count: measures.length })}</span>
      </div>

      <div className="music-volume">
        <button
          id="timeline-mute-soundtrack-btn"
          onClick={() => setMusicVolume(musicVolume > 0 ? 0 : 0.8)}
          style={{ padding: '2px', background: 'transparent', border: 'none' }}
          title={musicVolume > 0 ? t('music.mute') : t('music.unmute')}
        >
          {musicVolume > 0 ? <Volume2 size={12} color="#fcd34d" /> : <VolumeX size={12} color="#ef4444" />}
        </button>
        <input
          id="timeline-volume-soundtrack-slider"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={musicVolume}
          onChange={(e) => setMusicVolume(Number(e.target.value))}
          style={{ width: 50 }}
          title={`Volume: ${Math.round(musicVolume * 100)}%`}
        />
      </div>

      {!isReadOnly && (
        <button
          id="timeline-remove-soundtrack-btn"
          className="timeline-btn red"
          onClick={handleRemoveMusic}
          disabled={isPlaying}
          title={t('music.remove')}
          style={{ padding: '2px 4px', width: '24px', minWidth: '24px', height: '24px' }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};
